import formatNumber from './format-number';
import { formatThemeNames, isTruthyLimitValue } from './check-limits';
import productUtils from './ProductUtils';
import {
    PLAN_BLOCKER_CATEGORIES,
    PLAN_REQUIREMENTS_MODES,
    ADMIN_NAVIGATION_DESTINATIONS,
    ROUTES,
} from './constants';
import {
    REQUIREMENT_COPY,
    AUDIENCE_MODAL_COPY,
    STANDARD_MODAL_COPY,
} from './plan-requirements-copy';

/**
 * @typedef {import('./ProductUtils').PlanBlocker} PlanBlocker
 */

/**
 * Kinds of navigation action a requirement can resolve to. Structured
 * metadata the modal renders as an accessible action; never inferred from
 * copy. `admin-settings` hands a semantic destination to Ghost Admin,
 * `billing-route` navigates within the Billing App itself.
 * @type {{ADMIN_SETTINGS: 'admin-settings', BILLING_ROUTE: 'billing-route'}}
 */
export const REQUIREMENT_ACTION_KINDS = {
    ADMIN_SETTINGS: 'admin-settings',
    BILLING_ROUTE: 'billing-route',
};

/**
 * @typedef {object} RequirementAction
 * @property {'admin-settings'|'billing-route'} kind - discriminant
 * @property {string} [destination] - admin-settings only, a semantic
 *   ADMIN_NAVIGATION_DESTINATIONS value Ghost Admin maps to a route
 * @property {string} [route] - billing-route only, a Billing App ROUTES path
 */

/**
 * @typedef {object} RequirementItem
 * @property {string} limit - blocker key, stable id for rendering
 * @property {string} title - bold item heading
 * @property {string} body - sentence(s) with **bold** destination markers
 * @property {RequirementAction|null} action - structured navigation target,
 *   null for purely informational requirements
 */

/**
 * Navigation action metadata keyed by blocker `limit`. Kept beside the copy
 * lookup in buildRequirementItem: destinations live here as structured data,
 * customer-facing wording lives in REQUIREMENT_COPY, and the two never derive
 * from each other. Limits absent from this map are informational (their copy
 * routes to support/concierge or describes a structural constraint) and
 * resolve to a null action.
 * @type {Record<string, RequirementAction>}
 */
const REQUIREMENT_ACTIONS = {
    customThemes: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.THEME,
    },
    limitAnalytics: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.ANALYTICS,
    },
    staff: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.STAFF,
    },
    newsletters: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.NEWSLETTERS,
    },
    limitAutomations: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.AUTOMATIONS,
    },
    limitStripeConnect: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.STRIPE,
    },
    customIntegrations: {
        kind: REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS,
        destination: ADMIN_NAVIGATION_DESTINATIONS.INTEGRATIONS,
    },
    customSendingDomain: {
        kind: REQUIREMENT_ACTION_KINDS.BILLING_ROUTE,
        route: ROUTES.SENDING_DOMAIN,
    },
};

/**
 * @typedef {object} RequirementsViewModel
 * @property {'audience'|'standard'} mode - audience blockers take precedence
 *   while preserving concurrent non-audience requirements
 * @property {string} title
 * @property {string} sub - subtitle line, may carry **bold** markers
 * @property {string|null} note - audience mode only
 * @property {RequirementItem[]} items - non-audience requirements
 */

/**
 * Finds the lowest plan that includes a feature gated by a truthy limit
 * flag, for "comes with {higherTier} and up" copy. Derived from product
 * config rather than hardcoded so it follows lineup changes.
 *
 * @param {object[]} availableProducts - sorted by order, entry plan first
 * @param {string} limitName - e.g. 'limitAnalytics', 'limitStripeConnect'
 * @returns {string} product name, or a generic fallback
 */
export function findHigherTier(availableProducts, limitName) {
    const products = availableProducts || [];

    // Entry-plan-only gates (e.g. the sending domain) exist in no product's
    // limits config at all: every plan above the entry plan supports them
    const anyProductHasEntry = products.some((product) =>
        (product?.limits || []).some((entry) => entry.name === limitName),
    );
    if (!anyProductHasEntry) {
        return products[1]?.name || 'a higher plan';
    }

    const firstWithout = products.find((product) => {
        const limit = (product?.limits || []).find((entry) => entry.name === limitName);
        return !limit || !isTruthyLimitValue(limit.value);
    });

    return firstWithout?.name || 'a higher plan';
}

/**
 * @param {object[]} productLimits - the product's `limits` array
 * @param {string} name
 * @returns {*}
 */
const getProductLimitValue = (productLimits, name) => {
    return (productLimits || []).find((limit) => limit.name === name)?.value;
};

/**
 * Builds the copy variables and renders one requirement list item for a
 * blocker.
 *
 * @param {PlanBlocker} blocker
 * @param {object} context - see buildRequirementsViewModel
 * @returns {RequirementItem|null} null when the blocker has no copy (never
 *   expected; guards against future blocker keys without entries)
 */
function buildRequirementItem(blocker, context) {
    const copy = REQUIREMENT_COPY[blocker.limit];
    if (!copy) {
        return null;
    }

    const { productName, currentSiteLimits, productLimits, availableProducts } = context;

    /** @type {object} */
    const vars = { targetPlan: productName };

    if (blocker.limit === 'customThemes') {
        const requirement = String(getProductLimitValue(productLimits, 'customThemes') ?? '')
            .replace(/["']/g, '')
            .trim()
            .toLowerCase();
        vars.officialOnly = requirement.includes('official');
        vars.requiredTheme =
            requirement && !vars.officialOnly ? formatThemeNames(requirement) : null;
        const activeTheme = currentSiteLimits?.customThemes?.active;
        vars.currentTheme = activeTheme ? formatThemeNames(activeTheme) : null;
    }

    if (blocker.limit === 'staff') {
        const limitRaw = getProductLimitValue(productLimits, 'staff');
        const countRaw = currentSiteLimits?.staff?.total ?? 0;
        const overRaw = Math.max(countRaw - limitRaw, 0);
        Object.assign(vars, {
            staffLimit: formatNumber(limitRaw),
            staffLimitRaw: limitRaw,
            staffCount: formatNumber(countRaw),
            staffOver: formatNumber(overRaw),
            staffOverRaw: overRaw,
        });
    }

    if (blocker.limit === 'newsletters') {
        const limitRaw = getProductLimitValue(productLimits, 'newsletters');
        const countRaw = currentSiteLimits?.newsletters?.total ?? 0;
        const overRaw = Math.max(countRaw - limitRaw, 0);
        Object.assign(vars, {
            newsletterLimit: formatNumber(limitRaw),
            newsletterLimitRaw: limitRaw,
            newsletterCount: formatNumber(countRaw),
            newsletterOver: formatNumber(overRaw),
            newsletterOverRaw: overRaw,
        });
    }

    if (blocker.limit === 'limitAutomations') {
        const limitRaw = getProductLimitValue(productLimits, 'limitAutomations');
        const countRaw = currentSiteLimits?.limitAutomations?.total ?? 0;
        const overRaw = Math.max(countRaw - limitRaw, 0);
        Object.assign(vars, {
            automationLimit: formatNumber(limitRaw),
            automationLimitRaw: limitRaw,
            automationCount: formatNumber(countRaw),
            automationOver: formatNumber(overRaw),
            automationOverRaw: overRaw,
        });
    }

    if (blocker.limit === 'limitAnalytics') {
        vars.higherTier = findHigherTier(availableProducts, 'limitAnalytics');
    }

    if (blocker.limit === 'limitStripeConnect') {
        vars.higherTier = findHigherTier(availableProducts, 'limitStripeConnect');
    }

    if (blocker.limit === 'customSendingDomain') {
        vars.higherTier = findHigherTier(availableProducts, 'customSendingDomain');
    }

    return {
        limit: blocker.limit,
        title: copy.title(vars),
        body: copy.body(vars),
        action: REQUIREMENT_ACTIONS[blocker.limit] ?? null,
    };
}

/**
 * Builds everything the requirements modal renders for one blocked plan.
 *
 * When an audience-size blocker is present, render the focused audience
 * guidance alongside every concurrent self-serve and structural item.
 * Otherwise render the standard list with every remaining blocker.
 * `current-plan` blockers are card-state discriminators and never reach the
 * modal.
 *
 * @param {object} context
 * @param {PlanBlocker[]} context.blockers - from evaluateProductEligibility
 * @param {string} context.productName - the blocked (target) plan
 * @param {string|null} context.currentPlanName - user's plan, null on trial
 * @param {object} context.currentSiteLimits - site usage
 * @param {object[]} context.productLimits - the product's limits config
 * @param {object[]} context.prices - the product's prices (member cap source)
 * @param {string} context.showPeriod - selected billing period
 * @param {object[]} context.availableProducts
 * @param {string|null} context.recommendedPlanName - resolver result
 * @param {boolean} context.recommendedIsCurrentPlan - resolver result
 * @returns {RequirementsViewModel|null} null when nothing needs a modal
 */
export function buildRequirementsViewModel(context) {
    const {
        blockers,
        productName,
        currentPlanName,
        currentSiteLimits,
        prices,
        showPeriod,
        recommendedPlanName,
        recommendedIsCurrentPlan,
    } = context;

    const requirements = (blockers || []).filter(
        (blocker) => blocker.category !== PLAN_BLOCKER_CATEGORIES.CURRENT_PLAN,
    );
    if (!requirements.length) {
        return null;
    }

    const audienceBlocked = requirements.some(
        (blocker) => blocker.category === PLAN_BLOCKER_CATEGORIES.AUDIENCE,
    );

    if (audienceBlocked) {
        const memberCount = currentSiteLimits?.members?.total ?? 0;
        const fallbackPrice = productUtils.getFallbackPrice({ prices: prices || [], showPeriod });
        const memberLimit = fallbackPrice?.limits?.[0]?.value;

        let note;
        if (!recommendedPlanName) {
            note = AUDIENCE_MODAL_COPY.noteNoFit();
        } else if (recommendedIsCurrentPlan) {
            note = AUDIENCE_MODAL_COPY.noteCurrentPlan({ recommendedPlan: recommendedPlanName });
        } else {
            note = AUDIENCE_MODAL_COPY.note({ recommendedPlan: recommendedPlanName });
        }

        // When the outgrown plan is the user's own, acknowledge it ("You've
        // outgrown Starter") instead of the generic "fits a smaller audience"
        const outgrewCurrentPlan =
            Boolean(currentPlanName) &&
            productName?.toLowerCase() === currentPlanName?.toLowerCase();
        const audienceTitle = outgrewCurrentPlan
            ? AUDIENCE_MODAL_COPY.titleCurrentPlan({ targetPlan: productName })
            : AUDIENCE_MODAL_COPY.title({ targetPlan: productName });

        return {
            mode: PLAN_REQUIREMENTS_MODES.AUDIENCE,
            title: audienceTitle,
            sub: AUDIENCE_MODAL_COPY.sub({
                targetPlan: productName,
                memberCount: formatNumber(memberCount),
                memberLimit: formatNumber(memberLimit),
            }),
            note,
            items: requirements
                .filter((blocker) => blocker.category !== PLAN_BLOCKER_CATEGORIES.AUDIENCE)
                .map((blocker) => buildRequirementItem(blocker, context))
                .filter(Boolean),
        };
    }

    return {
        mode: PLAN_REQUIREMENTS_MODES.STANDARD,
        title: STANDARD_MODAL_COPY.title({ targetPlan: productName }),
        sub: STANDARD_MODAL_COPY.sub({ targetPlan: productName, currentPlan: currentPlanName }),
        note: null,
        items: requirements
            .map((blocker) => buildRequirementItem(blocker, context))
            .filter(Boolean),
    };
}
